import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { Upload, Image, message, Button } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import Iconfont from '@utils/Iconfont'
import Utils from '@utils/utils'
import API from "@api/api"
import styles from '../../../index.module.scss'
import PromptIcon from '@assets/images/svgIcon/promptIcon.svg'

const ImageItem = ({ item, index, onDelete }) => {
    const [visible, setVisible] = useState(false)

    return (
        <div className={styles.ImageCell} style={{ width: 128 }}>
            <img src={Utils.getImgThumbnail(item.url)} style={{ width: 128, height: 128 }} alt="" />
            <div className={styles.maskBox}>
                <p onClick={() => setVisible(true)}>
                    <Iconfont className={styles.Icon} type="icon-FullscreenOutlined" />
                    <span>查看大图</span>
                </p>
                <p onClick={() => onDelete(index)}>
                    <Iconfont className={styles.Icon} type="icon-DeleteOutlined" />
                    <span>删除图片</span>
                </p>
            </div>
            <span className={styles.imgTitle}>图{index + 1}</span>
            <Image
                style={{ display: 'none' }}
                src={Utils.getImgThumbnail(item.url)}
                preview={{
                    visible,
                    src: Utils.getImgThumbnail(item.url),
                    onVisibleChange: value => {
                        setVisible(value);
                    },
                }}
            />
        </div>
    )
}

const FusionWearBatch = ({ record, handleAddMore, projectId, setDataSource }) => {

    const handleDelete = async (index) => {
        const updatedInputImages = [...record.inputImages]
        updatedInputImages.splice(index, 1)

        const saveRes = await API.batchSaveSku([{
            ...record,
            inputImages: updatedInputImages,
            projectId
        }])

        if (saveRes?.code === 0) {
            setDataSource?.(prev => prev.map(it => it.skuId === record.skuId ? { ...it, inputImages: updatedInputImages } : it))
        }
    }

    return (
        <div className={`${styles.refListBox} ${record?.error ? styles.SelectModelBtnError : ''}`} style={{ maxWidth: 672 }}>
            {record?.inputImages?.map((item, index) => (
                <ImageItem key={index} item={item} index={index} onDelete={handleDelete} />
            ))}
            {
                record?.inputImages?.length < 10 && <div className={styles.uploadItem} >
                    <Upload
                        accept='image/jpeg,image/png,image/webp,image/avif'
                        listType="picture-card"
                        showUploadList={false}
                        multiple
                        beforeUpload={(file, fileList) => {
                            handleAddMore(file, fileList, record.skuId)
                            return false
                        }}
                    >
                        <Iconfont className={styles.Icon} type="icon-general_dd" />
                        <p>添加穿搭商品图</p>
                    </Upload>
                </div>
            }
        </div>
    )
}

export const FusionWearPrompt = ({ record, dataSource, setDataSource }) => {
    const [promptLoading, setPromptLoading] = useState(false)
    const [showPlaceholder, setShowPlaceholder] = useState(!record?.prompt)
    const contentEditableRef = useRef(null)
    const isComposing = useRef(false)
    const lastPromptRequestId = useRef(0)

    const KEYWORDS = useMemo(() => {
        const kws = ['模特', ...Array.from({ length: 10 }, (_, i) => `图${i + 1}`)];
        return kws.sort((a, b) => b.length - a.length);
    }, [])

    const formatText = useCallback((text) => {
        let cleanText = text.replace(/【|】/g, '');
        const pattern = new RegExp(`(${KEYWORDS.join('|')})`, 'g');
        return cleanText.replace(pattern, '<span class="keyword-highlight">$1</span>');
    }, [KEYWORDS])

    const saveSelection = (containerEl) => {
        const selection = window.getSelection();
        if (selection.rangeCount === 0) return null;
        const range = selection.getRangeAt(0);
        const preSelectionRange = range.cloneRange();
        preSelectionRange.selectNodeContents(containerEl);
        preSelectionRange.setEnd(range.startContainer, range.startOffset);
        const start = preSelectionRange.toString().length;
        return {
            start: start,
            end: start + range.toString().length
        };
    };

    const restoreSelection = (containerEl, savedSelection) => {
        if (!savedSelection) return;
        let charIndex = 0, range = document.createRange();
        range.setStart(containerEl, 0);
        range.collapse(true);
        let nodeStack = [containerEl], node, foundStart = false, stop = false;

        while (!stop && (node = nodeStack.pop())) {
            if (node.nodeType === 3) {
                const nextCharIndex = charIndex + node.length;
                if (!foundStart && savedSelection.start >= charIndex && savedSelection.start <= nextCharIndex) {
                    range.setStart(node, savedSelection.start - charIndex);
                    foundStart = true;
                }
                if (foundStart && savedSelection.end >= charIndex && savedSelection.end <= nextCharIndex) {
                    range.setEnd(node, savedSelection.end - charIndex);
                    stop = true;
                }
                charIndex = nextCharIndex;
            } else {
                let i = node.childNodes.length;
                while (i--) {
                    nodeStack.push(node.childNodes[i]);
                }
            }
        }
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
    };

    const handleContentInput = useCallback((e) => {
        let text = e.target.innerText;
        const isEmpty = text.replace(/\n/g, '').length === 0;
        setShowPlaceholder(isEmpty);

        if (isComposing.current) return;

        // 限制800字
        if (text.length > 800) {
            text = text.slice(0, 800);
            message.warning("最多输入800字");
        }

        // 处理清空的情况
        if (isEmpty) {
            if (e.target.innerHTML !== "") {
                e.target.innerHTML = "";
            }
            return;
        }

        const selection = saveSelection(e.target);
        const newHTML = formatText(text);

        if (e.target.innerHTML !== newHTML) {
            e.target.innerHTML = newHTML;
            restoreSelection(e.target, selection);
        }
    }, [formatText])

    const handleCompositionStart = () => {
        isComposing.current = true;
    }

    const handleCompositionEnd = (e) => {
        isComposing.current = false;
        handleContentInput(e);
    }

    const getInputValue = useCallback(() => {
        if (!contentEditableRef.current) return "";
        let text = contentEditableRef.current.innerText;
        let cleanText = text.replace(/【|】/g, '');
        const pattern = new RegExp(`(${KEYWORDS.join('|')})`, 'g');
        return cleanText.replace(pattern, '【$1】');
    }, [KEYWORDS])

    useEffect(() => {
        if (contentEditableRef.current) {
            const initialText = record?.prompt || "";
            // 避免重复设置导致光标跳动（虽然record改变会重置，但这里主要用于初始化）
            if (contentEditableRef.current.innerText.replace(/【|】/g, '') !== initialText.replace(/【|】/g, '')) {
                contentEditableRef.current.innerHTML = formatText(initialText);
                setShowPlaceholder(initialText.length === 0)
            }
        }
    }, [formatText, record?.prompt])

    const getAlgoPrompt = () => {
        const requestId = ++lastPromptRequestId.current
        setPromptLoading(true)
        setShowPlaceholder(false)
        API.getAlgoPrompt({
            images: record?.inputImages?.map(item => item.url) || [],
            modelImage: record?.modelList?.[0]?.url
        }).then(res => {
            if (requestId !== lastPromptRequestId.current) return
            if (res?.code == 0) {
                const val = res.data || ''
                if (contentEditableRef.current) contentEditableRef.current.innerHTML = formatText(val)
                handleBlur(val)
            } else {
                throw new Error(res.message)
            }
        }).catch(err => {
            if (requestId !== lastPromptRequestId.current) return
            message.error(err.message)
        }).finally(() => {
            if (requestId === lastPromptRequestId.current) {
                setPromptLoading(false)
            }
        })
    }

    const handleBlur = (val) => {
        const promptVal = typeof val === 'string' ? val : getInputValue()
        if (promptVal === record.prompt) return

        if (setDataSource) {
            setDataSource(prev => prev.map(item => {
                if (item.skuId === record.skuId) {
                    return {
                        ...item,
                        prompt: promptVal
                    }
                }
                return item
            }))
        }
    }

    return (
        <div className={styles.inputContainer} style={{ height: record?.inputImages?.length >= 5 ? 264 : 128 }}>
            {showPlaceholder && (
                <div className={styles.placeholderOverlay}>
                    请描述想要生成的画面，或点击
                    <span onClick={getAlgoPrompt} className={styles.smartPromptLink}>
                        {promptLoading ? <LoadingOutlined /> : '智能提示词'}
                    </span>
                    自动生成
                </div>
            )}
            <div
                ref={contentEditableRef}
                className={styles.contenteditableWrap}
                contentEditable
                onInput={handleContentInput}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                onBlur={() => handleBlur()}
                suppressContentEditableWarning
            />
            <Button
                loading={promptLoading}
                className={styles.smartPromptBtn}
                onClick={getAlgoPrompt}
            >
                {!promptLoading && <img style={{ width: 16 }} src={PromptIcon} alt="" />}
                智能提示词</Button>
        </div>
    )
}

export default FusionWearBatch